/**
 * Gift (Darovací smlouva) Contract Configuration
 */

import { ContractFormConfig, FieldType } from '../../types/contractForm.types';

export const giftContractConfig: ContractFormConfig = {
  type: 'gift',
  subtype: undefined,

  metadata: {
    title: 'Darovací smlouva',
    description: 'Konfigurace pro vytvoření darovací smlouvy',
    version: '1.0.0',
    lastModified: new Date().toISOString(),
  },

  // Note: Fields in step "Co se daruje?" are conditionally rendered based on gift_category
  // Movitá věc | Nemovitost | Peníze | Ostatní. See conditional clauses on fields below.

  // Before submit, strip out values that don't belong to the selected category
  transformData: (data) => {
    const cat = data?.gift_category;
    if (!cat) return data;

    const keepCommon = ['gift_category', 'gift_value'];

    const movable = [
      'movable_description',
      'movable_brand',
      'movable_model',
      'movable_serial_number',
      'movable_registration_plate',
      'movable_vin',
    ];
    const realEstate = [
      'real_estate_description',
      'real_estate_address',
      'cadastral_info',
      'real_estate_parcel_numbers',
      'real_estate_cadastral_unit',
      'real_estate_lv',
      'encumbrances',
    ];
    const money = [
      'money_amount',
      'bank_account_iban',
      'bank_account_number',
      'bank_account_bank_code',
      'variable_symbol',
      'specific_symbol',
      'constant_symbol',
    ];
    const other = ['gift_description'];

    let keep = keepCommon.slice();
    if (cat === 'movitost') keep = keep.concat(movable);
    else if (cat === 'nemovitost') keep = keep.concat(realEstate);
    else if (cat === 'penize') keep = keep.concat(money);
    else if (cat === 'ostatni') keep = keep.concat(other);

    const cleaned: Record<string, any> = {};
    Object.keys(data || {}).forEach((k) => {
      // Always keep donor/recipient and general info
      if (
        k.startsWith('donor_') ||
        k.startsWith('recipient_') ||
        k === 'transfer_date' ||
        k === 'notary_required' ||
        k === 'tax_obligations' ||
        k === 'gift_reason' ||
        k === 'special_conditions' ||
        keep.includes(k)
      ) {
        cleaned[k] = (data as any)[k];
      }
    });

    return cleaned as any;
  },

  steps: [
    {
      title: 'Kdo daruje?',
      subtitle: 'Údaje o dárci',
      description: 'Identifikace dárce (fyzická nebo právnická osoba)',
      icon: 'VolunteerActivism',
      color: 'primary',
      fields: [
        { name: 'donor_name', label: 'Jméno a příjmení / Název firmy', type: FieldType.TEXT, validation: [{ type: 'required', message: 'Jméno dárce je povinné' }], gridProps: { xs: 12 } },
        { name: 'donor_address', label: 'Adresa', type: FieldType.MULTILINE, rows: 2, validation: [{ type: 'required', message: 'Adresa dárce je povinná' }], gridProps: { xs: 12 } },
        { name: 'donor_id', label: 'Rodné číslo / IČO', type: FieldType.TEXT, validation: [ { type: 'required', message: 'Rodné číslo nebo IČO je povinné' }, { type: 'pattern', value: /^(\d{6}\/?\d{4}|\d{8})$/, message: 'Neplatný formát RČ/IČO' } ], gridProps: { xs: 12, md: 6 } },
        { name: 'donor_email', label: 'E‑mail (volitelné)', type: FieldType.EMAIL, gridProps: { xs: 12, md: 6 } },
      ],
    },
    {
      title: 'Kdo přijímá?',
      subtitle: 'Údaje o obdarovaném',
      description: 'Identifikace obdarovaného',
      icon: 'Person',
      color: 'secondary',
      fields: [
        { name: 'recipient_name', label: 'Jméno a příjmení / Název firmy', type: FieldType.TEXT, validation: [{ type: 'required', message: 'Jméno obdarovaného je povinné' }], gridProps: { xs: 12 } },
        { name: 'recipient_address', label: 'Adresa', type: FieldType.MULTILINE, rows: 2, validation: [{ type: 'required', message: 'Adresa obdarovaného je povinná' }], gridProps: { xs: 12 } },
        { name: 'recipient_id', label: 'Rodné číslo / IČO', type: FieldType.TEXT, validation: [ { type: 'required', message: 'Rodné číslo nebo IČO je povinné' }, { type: 'pattern', value: /^(\d{6}\/?\d{4}|\d{8})$/, message: 'Neplatný formát RČ/IČO' } ], gridProps: { xs: 12, md: 6 } },
        { name: 'recipient_email', label: 'E‑mail (volitelné)', type: FieldType.EMAIL, gridProps: { xs: 12, md: 6 } },
      ],
    },
    {
      title: 'Co se daruje?',
      subtitle: 'Popis daru a hodnota',
      description: 'Základní informace o předmětu daru',
      icon: 'CardGiftcard',
      color: 'warning',
      fields: [
        // Kategorie daru řídí, které vstupy se zobrazí
        { 
          name: 'gift_category', 
          label: 'Kategorie', 
          type: FieldType.SELECT, 
          options: [ 
            { value: 'movitost', label: 'Movitá věc' }, 
            { value: 'nemovitost', label: 'Nemovitost' }, 
            { value: 'penize', label: 'Peníze' }, 
            { value: 'ostatni', label: 'Ostatní' } 
          ], 
          validation: [{ type: 'required', message: 'Zvolte kategorii daru' }],
          gridProps: { xs: 12, md: 6 } 
        },

        // MOVITÁ VĚC
        { 
          name: 'movable_description', 
          label: 'Popis movité věci', 
          type: FieldType.MULTILINE, 
          rows: 3, 
          validation: [{ type: 'required', message: 'Popis movité věci je povinný' }], 
          conditional: { field: 'gift_category', operator: 'equals', value: 'movitost' },
          gridProps: { xs: 12 } 
        },
        { 
          name: 'movable_brand', 
          label: 'Značka (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'movitost' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'movable_model', 
          label: 'Model (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'movitost' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'movable_serial_number', 
          label: 'Výrobní číslo (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'movitost' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'movable_registration_plate', 
          label: 'Registrační značka (volitelné)', 
          type: FieldType.REGISTRATION_PLATE, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'movitost' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'movable_vin', 
          label: 'VIN (volitelné)', 
          type: FieldType.VIN, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'movitost' },
          gridProps: { xs: 12, md: 6 } 
        },

        // NEMOVITOST
        { 
          name: 'real_estate_description', 
          label: 'Popis nemovitosti', 
          type: FieldType.MULTILINE, 
          rows: 3, 
          validation: [{ type: 'required', message: 'Popis nemovitosti je povinný' }], 
          conditional: { field: 'gift_category', operator: 'equals', value: 'nemovitost' },
          gridProps: { xs: 12 } 
        },
        { 
          name: 'real_estate_address', 
          label: 'Adresa nemovitosti (volitelné)', 
          type: FieldType.MULTILINE, 
          rows: 2,
          conditional: { field: 'gift_category', operator: 'equals', value: 'nemovitost' },
          gridProps: { xs: 12 } 
        },
        { 
          name: 'cadastral_info', 
          label: 'Katastrální údaje (parcela/číslo LV, k.ú.)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'nemovitost' },
          gridProps: { xs: 12 } 
        },
        { 
          name: 'real_estate_parcel_numbers', 
          label: 'Parcelní čísla (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'nemovitost' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'real_estate_cadastral_unit', 
          label: 'Katastrální území (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'nemovitost' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'real_estate_lv', 
          label: 'Číslo listu vlastnictví (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'nemovitost' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'encumbrances', 
          label: 'Břemena a omezení (volitelné)', 
          type: FieldType.MULTILINE, 
          rows: 2, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'nemovitost' },
          gridProps: { xs: 12 } 
        },

        // PENÍZE
        { 
          name: 'money_amount', 
          label: 'Darovaná částka', 
          type: FieldType.CURRENCY, 
          currency: 'Kč',
          validation: [{ type: 'required', message: 'Zadejte darovanou částku' }],
          conditional: { field: 'gift_category', operator: 'equals', value: 'penize' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'bank_account_iban', 
          label: 'IBAN (volitelné)', 
          type: FieldType.TEXT, 
          helperText: 'Např. CZ65 0800 0000 1920 0014 5399',
          conditional: { field: 'gift_category', operator: 'equals', value: 'penize' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'bank_account_number', 
          label: 'Číslo účtu (volitelné)', 
          type: FieldType.TEXT, 
          helperText: 'Ve formátu BBAN, např. 123456789/0100',
          conditional: { field: 'gift_category', operator: 'equals', value: 'penize' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'bank_account_bank_code', 
          label: 'Kód banky (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'penize' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'variable_symbol', 
          label: 'Variabilní symbol (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'penize' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'specific_symbol', 
          label: 'Specifický symbol (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'penize' },
          gridProps: { xs: 12, md: 6 } 
        },
        { 
          name: 'constant_symbol', 
          label: 'Konstantní symbol (volitelné)', 
          type: FieldType.TEXT, 
          conditional: { field: 'gift_category', operator: 'equals', value: 'penize' },
          gridProps: { xs: 12, md: 6 } 
        },

        // OSTATNÍ (obecný popis)
        { 
          name: 'gift_description', 
          label: 'Popis daru', 
          type: FieldType.MULTILINE, 
          rows: 3, 
          validation: [{ type: 'required', message: 'Popis daru je povinný' }], 
          conditional: { field: 'gift_category', operator: 'equals', value: 'ostatni' },
          gridProps: { xs: 12 } 
        },

        // Obecná odhadovaná hodnota (volitelná) – ponecháno univerzálně
        { name: 'gift_value', label: 'Hodnota daru (Kč, volitelné)', type: FieldType.NUMBER, gridProps: { xs: 12, md: 6 } },
      ],
    },
    {
      title: 'Předání a formality',
      subtitle: 'Předání, notář, daně',
      description: 'Detaily ohledně předání a formálních náležitostí',
      icon: 'Schedule',
      color: 'info',
      fields: [
        { name: 'transfer_date', label: 'Datum předání (volitelné)', type: FieldType.DATE, gridProps: { xs: 12, md: 6 } },
        { name: 'notary_required', label: 'Notářské ověření podpisů', type: FieldType.CHECKBOX, gridProps: { xs: 12, md: 6 } },
        { name: 'tax_obligations', label: 'Daňové povinnosti (volitelné)', type: FieldType.MULTILINE, rows: 2, gridProps: { xs: 12 } },
        { name: 'gift_reason', label: 'Důvod darování (volitelné)', type: FieldType.TEXT, gridProps: { xs: 12 } },
        { name: 'special_conditions', label: 'Zvláštní ujednání (volitelné)', type: FieldType.MULTILINE, rows: 2, gridProps: { xs: 12 } },
      ],
    },
  ],
};

export default giftContractConfig;
